import * as React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  Link,
  TextField,
  Switch,
  Snackbar,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

// helpers import
import { formatToUsDate } from "../../utils/DateHelper";
import { snakeCaseToTitleCaseSingularize } from "../../utils/CaseFormatHelper";
import { Alert } from "@mui/material";

// app setting imports
import { AuthenticationContext, ImagesContext } from "../../Context";
import { getHeaders } from "../../utils/HeaderHelper";
import { checkPrivileges } from "../../utils/PrivilegesHelper";
import { PrivilegesContext } from "../../PrivilegesContext";
import { useFlash } from "../../FlashContext";

interface Props {
  csrfToken: string;
  menu_track_src: string;
  sort_plain_src: string;
  sort_ascending_src: string;
  sort_descending_src: string;
  selectedProvider: any;
  setSelectedProvider: any;
  setTempSelectedProvider: any;
  unsaveChanges: boolean;
  setUnsavedModalOpen: any;
  openAddProviderModal: any;
  reloadKey?: number;
}

const CoreTeamShow: React.FC<Props> = (props: any) => {
  //  context import
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const userPrivileges = React.useContext(PrivilegesContext);

  // other states
  const [sortObject, setSortObject] = React.useState<any>({
    field: "created_at",
    direction: "descending",
  });
  const [search, setSearch] = React.useState<string>("");

  // pagination states
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [pageLinks, setPageLinks] = React.useState<any>([]);
  const [paginatedList, setPaginatedList] = React.useState<any>([]);
  const perPage = 20;

  // fullProviders are actual results from API call, providers are answer showing on page
  const [providers, setProviders] = React.useState<any>([]);
  const [fullProviders, setFullProviders] = React.useState<any>([]);

  // filter_controller
  const [showBasicSearch, setShowBasicSearch] = React.useState<boolean>(true);
  const [advFirstName, setAdvFirstName] = React.useState<string>("");
  const [advLastName, setAdvLastName] = React.useState<string>("");
  const [showDeactivatedProviders, setShowDeactivatedProviders] =
    React.useState<boolean>(false);
  const { showToast } = useFlash();
  const afterFetch = React.useRef(false);

  React.useEffect(() => {
    getProviders();
  }, [props.reloadKey, showDeactivatedProviders]);

  const getProviders = () => {
    fetch(
      `/data_fetching/core_teams?show_deactivated_providers=${showDeactivatedProviders}`,
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.message);
        } else {
          setFullProviders(result?.resource?.providers);
          setProviders(result?.resource?.providers);
          setPaginatedList(paginateList(result?.resource?.providers));
          if (result?.resource?.providers?.length >= 1) {
            props.setSelectedProvider(result?.resource?.providers[0]);
            props.setTempSelectedProvider(result?.resource?.providers[0]);
          }
        }
        afterFetch.current = true;
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const advancedSearch = () => {
    event.preventDefault();
    fetch(
      `/data_fetching/core_teams?first_name=${advFirstName}&last_name=${advLastName}&show_deactivated_providers=${showDeactivatedProviders}`,
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.message);
        } else {
          setProviders(result?.resource?.providers);
          setSearch(""); //clears basic search
          setCurrentPage(1);
          if (result?.resource?.providers?.length >= 1) {
            props.setSelectedProvider(result?.resource?.providers[0]);
            props.setTempSelectedProvider(result?.resource?.providers[0]);
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  React.useEffect(() => {
    if (providers) {
      let pages = providers.length / perPage;
      setPageCount(Math.ceil(pages));
      const pageLinks = [];
      for (let i = 1; i <= pages + 1; i++) {
        pageLinks.push(
          <Link
            key={i}
            onClick={() => {
              selectPage(i);
            }}
            className={currentPage == i ? "linkDark" : "linkLight"}
          >
            {i}
          </Link>
        );
      }
      setPageLinks(pageLinks);
    }
  }, [providers, currentPage]);

  // Basic search
  React.useEffect(() => {
    basicSearch();
  }, [search]);

  const basicSearch = () => {
    if (fullProviders) {
      let providerList = getFilteredList(fullProviders);
      setProviders(providerList);
      setCurrentPage(1);
      clearAdvancedSearch();
    }
  };

  const getFilteredList = (providerListParam) => {
    let providerList = providerListParam.filter(
      (provider) =>
        provider.last_name.toLowerCase().indexOf(search.toLowerCase()) !== -1
    );
    return providerList;
  };

  React.useEffect(() => {
    if (providers) {
      sortList();
    }
  }, [sortObject]);

  const selectPage = (page) => {
    setCurrentPage(page);
  };

  const sortList = () => {
    const providerList = getSortedAndSearchedList();
    if (afterFetch.current == false) {
      props.setSelectedProvider(providerList[0]);
      props.setTempSelectedProvider(providerList[0]);
    }
    setProviders(providerList);
    setCurrentPage(1);
  };

  const getSortedAndSearchedList = () => {
    let providerList = [...providers];
    providerList.sort((a, b) => (a.id > b.id ? 1 : -1));
    if (sortObject.field == "last_sign_in_at") {
      providerList.sort((a, b) =>
        new Date(a["last_sign_in_at"]) > new Date(b["last_sign_in_at"]) ? 1 : -1
      );
    }

    if (sortObject.field == "created_at") {
      providerList.sort((a, b) =>
        new Date(a["created_at"]) > new Date(b["created_at"]) ? 1 : -1
      );
    }

    if (sortObject.field == "last_name") {
      providerList.sort((a, b) =>
        a["last_name"]?.toLowerCase() > b["last_name"]?.toLowerCase() ? 1 : -1
      );
    }

    if (sortObject.field == "role") {
      providerList.sort((a, b) =>
        a?.role?.toLowerCase() > b?.role?.toLowerCase() ? 1 : -1
      );
    }

    if (sortObject.field == "status") {
      providerList.sort((a, b) =>
        a?.invitation_accepted > b?.invitation_accepted ? 1 : -1
      );
    }

    if (sortObject.direction == "descending") {
      providerList.reverse();
    }

    if (search) {
      providerList = getFilteredList(providerList);
    }
    return providerList;
  };

  const setSortOrder = (sortBy, direction) => {
    let sort = {
      field: sortBy,
      direction: direction,
    };
    setSortObject(sort);
  };

  const paginateList = (list) => {
    if (list.length == 0) {
      return [];
    } else {
      let providerList = list;
      if (currentPage == 1) {
        return providerList.slice(0, perPage);
      } else {
        let offsetIndex = perPage * (currentPage - 1);
        return providerList.slice(offsetIndex, offsetIndex + perPage);
      }
    }
  };

  const updatePagination = () => {
    setPaginatedList(paginateList(providers));
  };

  React.useEffect(() => {
    updatePagination();
  }, [providers, currentPage]);

  const clearAdvancedSearch = () => {
    setAdvLastName("");
    setAdvFirstName("");
  };

  const handleSwitchChange = () => {
    if (showBasicSearch) {
      setShowBasicSearch(false);
      setSearch("");
    } else {
      setShowBasicSearch(true);
      basicSearch();
    }
  };

  const handleShowDeactivatedSwitch = () => {
    setShowDeactivatedProviders(!showDeactivatedProviders);
  };

  const handleProviderSelection = (user) => {
    if (props.unsaveChanges) {
      props.setTempSelectedProvider(user);
      props.setUnsavedModalOpen(true);
    } else {
      props.setSelectedProvider(user);
      props.setTempSelectedProvider(user);
    }
  };

  const processResendInvite = (userId) => {
    fetch(`/resend_provider_invitation/${userId}`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.error);
          showToast({ title: "Error", text: result.message, type: "error" });
        } else {
          showToast({ title: "Success", text: result.message, type: "success" });
          getProviders();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const processResendReinstate = (userId) => {
    fetch(`/resend_reinstate/${userId}`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.error);
          showToast({ title: "Error", text: result.message, type: "error" });
        } else {
          showToast({ title: "Success", text: result.message, type: "success" });
          getProviders();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <Grid item xs={6} className="patient-index-container">
        <Grid container className="patient-search-container">
          <Grid container>
            <Grid item xs={12}>
              <p className="search-form-label">PROVIDER SEARCH</p>
            </Grid>
          </Grid>
          <Grid container justifyContent="space-between">
            {showBasicSearch ? (
              <Grid item xs={6}>
                <TextField
                  id="search"
                  className="textInput plainLabel"
                  label="Last Name"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                  }}
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  size="small"
                />
              </Grid>
            ) : (
              <Grid item xs={6}>
                <Grid container justifyContent="space-between" spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      id="search"
                      className="textInput plainLabel"
                      label="Last Name"
                      value={advLastName}
                      onChange={(event) => {
                        setAdvLastName(event.target.value);
                      }}
                      variant="filled"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      size="small"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          advancedSearch();
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      id="search"
                      className="textInput plainLabel"
                      label="First Name"
                      value={advFirstName}
                      onChange={(event) => {
                        setAdvFirstName(event.target.value);
                      }}
                      variant="filled"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      size="small"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          advancedSearch();
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={5} className="search-switch">
              <div className="switch">
                BASIC SEARCH
                <Switch
                  checked={!showBasicSearch}
                  onChange={handleSwitchChange}
                  color="primary"
                />
                ADVANCED SEARCH
              </div>

              {!showBasicSearch && (
                <div className="search-button-container up-10px">
                  <Link className="search-button" onClick={advancedSearch}>
                    Search
                  </Link>
                </div>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          className="panel-container"
          borderRadius={"4px"}
          style={{ backgroundColor: "transparent", marginTop: "0px" }}
        >
          <Grid item xs={12} style={{ marginRight: "30px" }}>
            <Grid container className="panel-show-container">
              <Grid container className="panel-information-container">
                <Grid container direction="row" className="admin-header">
                  <Grid item xs={12} className="box-header">
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      paddingX={3}
                      paddingY={1}
                      alignItems={"center"}
                      display={"flex"}
                    >
                      <Grid item>
                        <Stack
                          direction="row"
                          columnGap="12px"
                          alignItems="center"
                        >
                          <Link
                            style={{
                              textDecoration: "none",
                              display: "flex",
                              alignItems: "center",
                            }}
                            href={`/providers-and-organizations`}
                          >
                            <ArrowBackIosNewIcon
                              style={{
                                borderRadius: "3px",
                                padding: "8px",
                                color: "black",
                                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                fontSize: "14px",
                              }}
                            />
                          </Link>
                          <p className="panel-header">Provider List</p>
                        </Stack>
                      </Grid>
                      <Grid item>
                        <Stack direction="row">
                          {checkPrivileges(
                            userPrivileges,
                            "Invite New Provider"
                          ) && (
                            <Link
                              className="action-link row"
                              style={{ backgroundColor: "transparent" }}
                              onClick={props.openAddProviderModal}
                            >
                              <img
                                src={images.add_icon}
                                alt="Add Charge"
                                className="action-icon-image"
                                style={{ width: 20, maxHeight: 20 }}
                              />
                              <span className="app-user-text disable-pointer">
                                Add Provider
                              </span>
                            </Link>
                          )}
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Switch
                              checked={showDeactivatedProviders}
                              onChange={handleShowDeactivatedSwitch}
                              color="primary"
                            />
                            <Typography
                              className="app-user-text disable-pointer"
                              style={{ marginLeft: "0px" }}
                            >
                              Show Deactivated Providers
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    </Stack>
                  </Grid>
                </Grid>
                <Grid container className="general-patient-info-container">
                  <Grid
                    item
                    xs={12}
                    className="info-container"
                    style={{ padding: "0px" }}
                  >
                    <TableContainer>
                      <Table>
                        <TableHead className="table-header-box">
                          <TableRow>
                            <TableCell
                              align="left"
                              onClick={() => {
                                setSortOrder(
                                  "last_name",
                                  sortObject.direction == "ascending"
                                    ? "descending"
                                    : "ascending"
                                );
                              }}
                              className="table-header-cell"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <strong
                                  style={{ fontFamily: "QuicksandMedium" }}
                                >
                                  Providers ({providers?.length})
                                </strong>

                                {sortObject.field == "last_name" ? (
                                  <span className="sortIndicator provider">
                                    {sortObject.direction == "ascending" ? (
                                      <img
                                        src={props.sort_ascending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    ) : (
                                      <img
                                        src={props.sort_descending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Desc"
                                      />
                                    )}
                                  </span>
                                ) : (
                                  <span className="sortIndicator provider">
                                    <img
                                      src={props.sort_plain_src}
                                      width="10"
                                      className="sort-icon"
                                      alt="Sort Asc"
                                    />
                                  </span>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="left"
                              onClick={() => {
                                setSortOrder(
                                  "role",
                                  sortObject.direction == "ascending"
                                    ? "descending"
                                    : "ascending"
                                );
                              }}
                              className="table-header-cell"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <strong
                                  style={{ fontFamily: "QuicksandMedium" }}
                                >
                                  Role
                                </strong>
                                {sortObject.field == "role" ? (
                                  <span className="sortIndicator provider">
                                    {sortObject.direction == "ascending" ? (
                                      <img
                                        src={props.sort_ascending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    ) : (
                                      <img
                                        src={props.sort_descending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Desc"
                                      />
                                    )}
                                  </span>
                                ) : (
                                  <span className="sortIndicator provider">
                                    <img
                                      src={props.sort_plain_src}
                                      width="10"
                                      className="sort-icon"
                                      alt="Sort Asc"
                                    />
                                  </span>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="left"
                              onClick={() => {
                                setSortOrder(
                                  "created_at",
                                  sortObject.direction == "ascending"
                                    ? "descending"
                                    : "ascending"
                                );
                              }}
                              className="table-header-cell"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <strong
                                  style={{ fontFamily: "QuicksandMedium" }}
                                >
                                  Date Joined
                                </strong>
                                {sortObject.field == "created_at" ? (
                                  <span className="sortIndicator provider">
                                    {sortObject.direction == "ascending" ? (
                                      <img
                                        src={props.sort_ascending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    ) : (
                                      <img
                                        src={props.sort_descending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Desc"
                                      />
                                    )}
                                  </span>
                                ) : (
                                  <span className="sortIndicator provider">
                                    <img
                                      src={props.sort_plain_src}
                                      width="10"
                                      className="sort-icon"
                                      alt="Sort Asc"
                                    />
                                  </span>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="left"
                              onClick={() => {
                                setSortOrder(
                                  "last_sign_in_at",
                                  sortObject.direction == "ascending"
                                    ? "descending"
                                    : "ascending"
                                );
                              }}
                              className="table-header-cell"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <strong
                                  style={{ fontFamily: "QuicksandMedium" }}
                                >
                                  Last Login
                                </strong>
                                {sortObject.field == "last_sign_in_at" ? (
                                  <span className="sortIndicator provider">
                                    {sortObject.direction == "ascending" ? (
                                      <img
                                        src={props.sort_ascending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    ) : (
                                      <img
                                        src={props.sort_descending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Desc"
                                      />
                                    )}
                                  </span>
                                ) : (
                                  <span className="sortIndicator provider">
                                    <img
                                      src={props.sort_plain_src}
                                      width="10"
                                      className="sort-icon"
                                      alt="Sort Asc"
                                    />
                                  </span>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="left"
                              onClick={() => {
                                setSortOrder(
                                  "status",
                                  sortObject.direction == "ascending"
                                    ? "descending"
                                    : "ascending"
                                );
                              }}
                              className="table-header-cell status-header-cell"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <strong
                                  style={{ fontFamily: "QuicksandMedium" }}
                                >
                                  {" "}
                                  Status{" "}
                                </strong>
                                {sortObject.field == "status" ? (
                                  <span className="sortIndicator provider">
                                    {sortObject.direction == "ascending" ? (
                                      <img
                                        src={props.sort_ascending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    ) : (
                                      <img
                                        src={props.sort_descending_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Desc"
                                      />
                                    )}
                                  </span>
                                ) : (
                                  <span className="sortIndicator provider">
                                    <img
                                      src={props.sort_plain_src}
                                      width="10"
                                      className="sort-icon"
                                      alt="Sort Asc"
                                    />
                                  </span>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        {paginatedList ? (
                          <TableBody>
                            {paginatedList.map((user, index) => (
                              <TableRow
                                key={index + 1}
                                sx={{ "& td, & th": { borderBottom: "none" } }}
                              >
                                <TableCell className="table-body-cell">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      columnGap: "5px",
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name="selectCustomer"
                                      onChange={() => {
                                        handleProviderSelection(user);
                                      }}
                                      checked={
                                        props.selectedProvider?.id == user.id
                                      }
                                    />
                                    {user.name_reversed}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  scope="row"
                                  align="left"
                                  className="table-body-cell"
                                >
                                  {snakeCaseToTitleCaseSingularize(user.career)}
                                </TableCell>
                                <TableCell
                                  scope="row"
                                  align="left"
                                  className="table-body-cell"
                                >
                                  {user.created_at
                                    ? formatToUsDate(user.created_at)
                                    : "N/A"}
                                </TableCell>
                                <TableCell
                                  scope="row"
                                  align="left"
                                  className="table-body-cell"
                                >
                                  {user.last_sign_in_at
                                    ? formatToUsDate(user.last_sign_in_at)
                                    : "N/A"}
                                </TableCell>
                                <TableCell
                                  className="status-body-cell"
                                  align="center"
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {!user.is_active &&
                                    user.status === "deactivated" ? (
                                      <Typography
                                        style={{
                                          backgroundColor: "#FFDBDB",
                                          color: "#FF0000",
                                          fontWeight: 600,
                                          fontSize: "10px",
                                          textAlign: "center",
                                          padding: "2px 12px 2px 12px",
                                          borderRadius: "10px",
                                        }}
                                      >
                                        Deactivated
                                      </Typography>
                                    ) : !user.is_active &&
                                      user.status === "pending_reinstate" ? (
                                      (() => {
                                        const resetPasswordSentAt = user.reset_password_sent_at
                                          ? new Date(user.reset_password_sent_at)
                                          : null;
                                        const sixHoursAgo = new Date(
                                          Date.now() - 6 * 60 * 60 * 1000
                                        );
                                        const canResend =
                                          !resetPasswordSentAt ||
                                          resetPasswordSentAt < sixHoursAgo;

                                        return canResend ? (
                                          <Link
                                            sx={{
                                              backgroundColor: "#FFF0BE",
                                              color: "#B08800",
                                              fontWeight: 600,
                                              fontSize: "10px",
                                              textAlign: "center",
                                              padding: "2px 12px 2px 12px",
                                              borderRadius: "10px",
                                              cursor: "pointer",
                                            }}
                                            onClick={() =>
                                              processResendReinstate(user.user_id)
                                            }
                                          >
                                            Resend Reinstate
                                          </Link>
                                        ) : (
                                          <Typography
                                            style={{
                                              backgroundColor: "#FFF0BE",
                                              color: "#B08800",
                                              fontWeight: 600,
                                              fontSize: "10px",
                                              textAlign: "center",
                                              padding: "2px 12px 2px 12px",
                                              borderRadius: "10px",
                                              width: "min-content",
                                            }}
                                          >
                                            Pending Reinstate
                                          </Typography>
                                        );
                                      })()
                                    ) : user.is_active &&
                                      user.invitation_accepted ? (
                                      <Typography
                                        style={{
                                          fontWeight: 600,
                                          fontSize: "10px",
                                          textAlign: "center",
                                        }}
                                      >
                                        Active
                                      </Typography>
                                    ) : (
                                      <Link
                                        sx={{
                                          backgroundColor: "#FFF0BE",
                                          color: "#B08800",
                                          fontWeight: 600,
                                          fontSize: "10px",
                                          textAlign: "center",
                                          padding: "2px 12px 2px 12px",
                                          borderRadius: "10px",
                                        }}
                                        onClick={() =>
                                          processResendInvite(user.user_id)
                                        }
                                      >
                                        Resend
                                      </Link>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        ) : (
                          afterFetch.current && <p>No users found.</p>
                        )}
                      </Table>
                    </TableContainer>
                    <p className="pagerLinks">
                      <Link
                        onClick={() => {
                          selectPage(
                            currentPage > 1 ? currentPage - 1 : currentPage
                          );
                        }}
                        className="linkDark"
                      >
                        &lt;
                      </Link>
                      {pageLinks}
                      <Link
                        onClick={() => {
                          selectPage(
                            currentPage < pageCount
                              ? currentPage + 1
                              : currentPage
                          );
                        }}
                        className="linkDark"
                      >
                        &gt;
                      </Link>
                    </p>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default CoreTeamShow;
